import { useMutation, useSuspenseQuery } from '@tanstack/react-query';

import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type {
  JobPositionCreateSchema,
  JobPositionQuerySchema,
  JobPositionsQueryAllSchema,
  JobPositionUpdateSchema,
} from './schemas';

export function useGetJobPositions(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['JOB_POSITIONS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<JobPositionsQueryAllSchema>({
        endpoint: '/api/v1/job-positions/',
        params: `${apiParamStr}&include=salary_grade,preferred_location,preferred_relationship,department,business_discipline`,
      });
      return res;
    },
  });
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['JOB_POSITIONS'] });
};

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, JobPositionCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/job-positions/',
        payload,
      }),
  });

export function useGetJobPosition(id: string) {
  return useSuspenseQuery(getJobPositionQuery(id));
}

export function getJobPositionQuery(id: string) {
  return {
    queryKey: ['JOB_POSITION', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: JobPositionQuerySchema }>({
        endpoint: `/api/v1/job-positions/${id}`,
        params: {
          include: 'salary_grade,preferred_location,preferred_relationship,department,business_discipline',
        },
        preventAuditLog: true,
      });

      return res;
    },
  };
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: JobPositionQuerySchema }, ApiError, Partial<JobPositionUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/job-positions/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['JOB_POSITION_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/job-positions/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });
